/**
 * Load reCAPTCHA v3 script once and obtain execute tokens.
 * @see https://developers.google.com/recaptcha/docs/v3
 */

let scriptLoadPromise = null;

function ensureRecaptchaScript(siteKey) {
  if (typeof window === 'undefined' || !siteKey) {
    return Promise.resolve();
  }
  if (window.grecaptcha?.execute) {
    return Promise.resolve();
  }
  if (!scriptLoadPromise) {
    scriptLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
      if (existing) {
        if (window.grecaptcha?.execute) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('reCAPTCHA script failed')), {
          once: true,
        });
        return;
      }
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
      document.head.appendChild(script);
    });
  }
  return scriptLoadPromise;
}

/**
 * @param {string} siteKey
 * @param {string} action - v3 action name (alphanumeric, underscore, slash only)
 * @returns {Promise<string|null>} Token or null if no site key / no window
 */
export async function getRecaptchaToken(siteKey, action = 'awatch') {
  if (typeof window === 'undefined' || !siteKey) {
    return null;
  }
  await ensureRecaptchaScript(siteKey);
  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha.execute(siteKey, { action });
        resolve(token);
      } catch (e) {
        reject(e);
      }
    });
  });
}
